import {
  Address,
  Billing,
  Category,
  Lister,
  Listing,
  OfferType,
} from '@smg-real-estate/hg-rets-schema';

export enum InsertionStatus {
  Draft = 'DRAFT',
  PendingApproval = 'PENDING_APPROVAL',
  Approved = 'APPROVED',
  Published = 'PUBLISHED',
  Rejected = 'REJECTED',
  Archived = 'ARCHIVED',
  Archiving = 'ARCHIVING',
  Republishing = 'REPUBLISHING',
  PendingRepublishApproval = 'PENDING_REPUBLISH_APPROVAL',
  Deleted = 'DELETED',
  ContactRequested = 'CONTACT_REQUESTED',
}

export enum Currency {
  CHF = 'CHF',
  EUR = 'EUR',
  USD = 'USD',
}

export enum AvailableFrom {
  BY_AGREEMENT = 'AGREEMENT',
  IMMEDIATELY = 'IMMEDIATELY',
  DATE = 'DATE',
}

export enum DeactivationScheduleTargetTerm {
  CURRENT = 'CURRENT',
  NEXT = 'NEXT',
}

export enum SupportedLanguage {
  DE = 'de',
  EN = 'en',
  FR = 'fr',
  IT = 'it',
}

export enum DeactivationReason {
  FOUND_ON_HOMEGATE = 'FOUND_ON_HOMEGATE',
  FOUND_ELSEWHERE = 'FOUND_ELSEWHERE',
  NOT_ENOUGH_INQUIRIES = 'NOT_ENOUGH_INQUIRIES',
  AD_TOO_EXPENSIVE = 'AD_TOO_EXPENSIVE',
  PUBLISHED_BY_BROKER = 'PUBLISHED_BY_BROKER',
}

export enum ScheduledDeactivationReason {
  EXPECT_FOUND_BEFORE_RENEWAL = 'EXPECT_FOUND_BEFORE_RENEWAL',
  EXPECT_FOUND_OTHER_CHANNEL = 'EXPECT_FOUND_OTHER_CHANNEL',
  NOT_ENOUGH_INQUIRIES_SO_FAR = 'NOT_ENOUGH_INQUIRIES_SO_FAR',
  LOW_PERCEIVED_VALUE = 'LOW_PERCEIVED_VALUE',
  USE_ANOTHER_BROKER = 'USE_ANOTHER_BROKER',
}

export enum SystemDeactivationReason {
  T2F_PERIOD_ENDED = 'T2F_PERIOD_ENDED',
  TENANT_DOC_NON_COMPLIANCE = 'TENANT_DOC_NON_COMPLIANCE',
}

export type DeactivationReasonKey =
  DeactivationReason | ScheduledDeactivationReason | SystemDeactivationReason | 'OTHER_REASON';

export enum PagesFlowOrder {
  DETAILS,
  BUNDLE,
  BILLING,
  CONFIRMATION,
}

export interface ArchivalReason {
  key: DeactivationReasonKey;
  reason: string;
}

export const RouteNameToPagesFlowOrder: Record<string, PagesFlowOrder> = {
  details: PagesFlowOrder.DETAILS,
  bundles: PagesFlowOrder.BUNDLE,
  billing: PagesFlowOrder.BILLING,
  confirmation: PagesFlowOrder.CONFIRMATION,
};

export enum SupportedPlatform {
  HOMEGATE = 'homegate',
  IMMOSCOUT24 = 'immoscout24',
}

export interface DeactivationSchedule {
  scheduledDate: string;
  scheduledAt: string;
  scheduledBy: string;
  archivalReason: ArchivalReason;
}

export type ListingLinks = Partial<{
  [platform in SupportedPlatform]: Record<SupportedLanguage, string>;
}>;

export enum PricingCampaignId {
  T2T = 'T2T',
  HEV = 'HEV',
}

export type DeviceCategory = 'desktop' | 'mobile' | 'tablet';
export type OperatingSystemName = 'Windows' | 'macOS' | 'iOS' | 'Android' | 'Linux';
export type BrowserName = 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'Opera';

export interface ClientMetaData {
  deviceCategory: DeviceCategory;
  browser?: BrowserName;
  browserVersion?: string;
  operatingSystem?: OperatingSystemName;
  operatingSystemVersion?: string;
  referrer?: string;
}

/** Funnel-only guest state from insertion API (`meta.guestInfo`). */
export interface InsertionGuestInfo {
  guestOwnerId?: string;
  accountEmail?: string;
}

export interface TenantDocMeta {
  fileName?: string;
  url?: string;
  uploadedAt?: string;
  sendReminderAt?: string;
  reminderSendOn?: string;
  thankYouEmailSentAt?: string;
}

export interface InsertionMeta {
  pricingCampaignId?: PricingCampaignId;
  isT2TFreeCrosslisting?: boolean;
  featureFlags?: Record<string, string | undefined>;
  deviceInfo?: ClientMetaData;
  guestInfo?: InsertionGuestInfo;
  billingContactId?: string;
  tenantDoc?: TenantDocMeta;
}

export interface PpaBilling extends Billing {
  payment?: {
    paymentType: PaymentType;
    zuoraSubscriptionNumber?: string;
  };
}

interface Appointment {
  editUrl: string | null;
  productReference: string;
  providerReference: string;
  resourceReference: string;
  userReference: string;
  uuid: string;
}

export interface Insertion {
  agencyId?: string;
  id: string;
  status: InsertionStatus;
  userId: string;
  username: string;
  flaggedForFakeCheck?: boolean;
  flaggedForFraud?: string;
  listingLinks?: ListingLinks;
  listing: Listing & {
    lister?: Lister & {
      billing?: PpaBilling;
    };
  };
  meta: InsertionMeta;
  createdAt: Date;
  rejectionMessage?: string;
  lastSavedHash: string;
  lastSavedAt?: Date;
  firstPublishedDate?: string;
  renewalPaymentDueDate?: Date;
  subscriptionResumableDate?: Date;
  selectedBundle?: SelectedBundle;
  deactivationSchedule?: DeactivationSchedule;
  contactRequestDate?: string;
  publishedInsertionsByOwner?: number;
  isContactEmailReused?: boolean;
  note?: string;
  autoApprovalCriteria?: AutoApprovalCriteria;
  customerSegment?: string;
  furthestPage?: string;
  objectReference: string;
  updatedAt?: string;
  appointment?: Appointment;
  sourcePlatform: string;
  shouldHideAddress?: boolean;
}

// Feel free to change. First draft.
export interface Bill {
  billId: string;
  product: BundleTier;
  amount: string;
  currency: Currency;
  status: string;
  billingPeriod: { start: string; end: string };
  objReference: string;
  invoiceNumber: string;
}

export interface QueryData {
  offer: 'SALE' | 'RENT';
  category?: string;
  type?: string;
  country?: string;
  zip?: string;
  city?: string;
  street?: string;
  canton?: string;
  userid?: string;
  'pricing-campaign-id'?: string;
  customerSegment?: string;
  listingOfferType?: OfferType;
  listingCategoriesMapped?: Category[];
  feature?: string;
  iframeReferrer?: string;
}

export enum UserGroup {
  PRIVATE = 'private',
  AGENCY = 'agency',
  CUSTOMER_SERVICE_AGENT = 'ccc',
  PERSONAL = 'personal',
}

export type UserId = string;

export interface AuthorizedUser {
  userId: UserId;
  email: string;
  logonId: string;
  groups: UserGroup[];
  picture?: string;
  name?: string;
  userType?: UserType;
  emailVerified?: boolean;
  isGuest?: boolean;
}

export interface CloudinarySignedUploadParams {
  signature: string;
  timestamp: string;
  api_key: string;
  public_id: string;
  uploadUrl: string;
}

export enum BundleTier {
  BASIC = 'basic',
  TOP = 'top',
  PREMIUM = 'premium',
}

export enum BundlePeriod {
  ONE_WEEK = 7,
  TWO_WEEKS = 14,
  FOUR_WEEKS = 28,
  EIGHT_WEEKS = 56,
}

export enum PricingCustomerSegment {
  T2T = 'T2T',
  SEMIPRO = 'SEMIPRO',
  PRIVATE = 'PRIVATE',
  T2F = 'T2F',
}

export interface BundleAddOns {
  newConstruction: boolean;
}

export interface SelectedBundle {
  id: string;
  tier: BundleTier;
  initialPrice: number;
  recurringPrice: number;
  recurringWeeklyPrice?: number;
  period?: BundlePeriod;
  oldPrice?: number;
  addOns?: BundleAddOns;
}

export interface StatusTransition {
  from: InsertionStatus;
  to: InsertionStatus;
  user?: string;
  timestamp: string;
}

export enum PaymentType {
  DIRECT = 'DIRECT',
  INVOICE = 'INVOICE',
}

export interface AutoApprovalCriteria {
  meta: any;
  criteria: AutoApprovalRules;
}

export interface AutoApprovalRules {
  seonApproved: boolean;
  hasUsedSameEmailBefore: boolean;
}

export enum LandingFormStep {
  CUSTOMER_SEGMENT,
  OFFER_TYPE,
  CATEGORY,
  TYPE,
  GEO_SECTION,
}

export interface AgencyDetails {
  name: Lister['name'];
  legalName: Lister['legalName'];
  logoUrl: Lister['logoUrl'];
  country: Address['country'];
  street: Address['street'];
  locality: Address['locality'];
  postalCode: Address['postalCode'];
}

/**
 * User from Insertion Funnel API
 */
export interface User {
  ownerId: string;
  userType: UserType;
}

/**
 * User Type from Insertion Funnel API
 */
export enum UserType {
  PRIVATE = 'PRIVATE',
  TENANT = 'TENANT',
  OWNER = 'OWNER',
  SEMI_PRO = 'SEMI_PRO',
  // Keep this value aligned with the Insertion Funnel API response.
  BUSINESS = 'BUSINESS',
}

export enum InvoicePaymentStatus {
  Open = 'Open',
  Paid = 'Paid',
  Closed = 'Closed',
  Overpaid = 'Overpaid',
}

export interface Invoice {
  name: string;
  invoicePeriod: string;
  totalAmount: string;
  billingDocUrl: string;
  objectReference: string;
  paymentStatus: InvoicePaymentStatus;
  billingAddress: string;
}

export interface RecentDraftInsertion {
  insertionId: string;
  objectReference: string;
  listing: Listing;
}

export enum FeatureFlagName {
  TEST_NAME = 'FeatureFlagName',
  DEACTIVATION_SCHEDULER_BUNDLE_FEATURE = 'PKP-4762_Deactivation-Scheduler-Bundle-Feature',
  DD4035_NAVIGATION_EXPERIMENT = 'DD-4035-Alternate-Header-Navigation-Links',
  HOW = 'HOW',
  CONTACT_SEEKERS_UPGRADE_FDT = 'PKP-6159_Contact-Seeker-FDT',
  T2F_FOREVER_FREE = 'PKP-4903_T2F-Forever-Free',
  NEW_LANDING_PAGE = 'PKP-5081_New-Landing-Page',
  REMOVE_INVOICE_FROM_BASIC = 'PKP-6157_Remove-Invoice-from-Basic',
  VIEW_DEVELOPMENT_GRAPH_MY_ADS = 'PKP-6082_View-Development-Graph-MyAds',
  NEW_CONSTRUCTION = 'PKP-5973_New-Construction-PPA',
}

export enum UserRedirectedNextStepTypes {
  PAYMENT = 'payment',
}

export interface UserRedirectedNextStep {
  type: UserRedirectedNextStepTypes;
  paymentType: PaymentType;
  feature?: string[];
  login_hint?: string;
}
